import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { api, formatDateTime } from "../../api";
import { StatusBadge } from "../Ui";
import { changeState, type FbChange, type FbLink } from "../../facebook";

/** Which linked account the page shows, kept in the URL (?link=). Hidden with one account. */
export function AccountPicker({ links, current, label }: { links: FbLink[]; current: FbLink; label: string }) {
  const [params, setParams] = useSearchParams();
  if (links.length < 2) return null;
  return (
    <select className="campaigns-account" aria-label={label} value={current.id}
      onChange={(e) => { const next = new URLSearchParams(params); next.set("link", e.target.value); setParams(next, { replace: true }); }}>
      {links.map((l) => <option key={l.id} value={l.id}>{l.account_name}</option>)}
    </select>
  );
}

/** Reads Facebook through Windsor now. Pages otherwise show what the 12-hourly read stored. */
export function SyncButton({ clientId, kind, onDone }: { clientId: number; kind: "ads" | "page"; onDone: (message: string, ok: boolean) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <button type="button" className="button secondary" disabled={busy} onClick={async () => {
      setBusy(true);
      try {
        const r = await api<{ message: string }>(`/api/facebook/${clientId}/sync`, { method: "POST", body: JSON.stringify({ kind }) });
        onDone(r.message, !/^Could not/.test(r.message));
      } catch (e) { onDone(e instanceof Error ? e.message : "Could not read Facebook.", false); }
      finally { setBusy(false); }
    }}><RefreshCw className={busy ? "spin" : ""} aria-hidden="true" />{busy ? "Reading Facebook…" : "Sync now"}</button>
  );
}

/** When the account was last read, and what went wrong if the last read failed. */
export function Freshness({ link }: { link: FbLink }) {
  if (!link.last_attempt_at) return <p className="fb-fresh">Not read from Facebook yet. Press Sync now.</p>;
  return (
    <p className="fb-fresh">
      {link.last_synced_at ? `Read from Facebook ${formatDateTime(link.last_synced_at)}.` : "No successful read yet."}
      {link.last_error ? <span className="fb-fresh-error"> {link.last_status === "failed" ? "Last try failed:" : "Not everything could be read:"} {link.last_error}</span> : null}
      {link.current_credentials === false && <span className="fb-fresh-error"> Linked with an older Windsor key: link it again in Setup, Connections.</span>}
    </p>
  );
}

/** Changes are a per-account switch (Setup, Connections). A superadmin can turn it on here. */
export function WritesNotice({ link, admin, clientId, onChanged, what }: { link: FbLink; admin: boolean; clientId: number; onChanged: () => void; what: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (link.writes_enabled) return null;
  return (
    <div className="fb-writes-off" role="status">
      <span>
        Changes from NOLA are off for {link.account_name}.{" "}
        {admin ? `Turn them on to ${what} from here. Every change is checked with Meta after it runs.` : "A superadmin turns them on."}
        {error && <span className="fb-fresh-error"> {error}</span>}
      </span>
      {admin
        ? <button type="button" className="button secondary" disabled={busy} onClick={async () => {
          setBusy(true); setError("");
          try { await api(`/api/windsor/links/${link.id}/writes`, { method: "PUT", body: JSON.stringify({ enabled: true }) }); onChanged(); }
          catch (e) { setError(e instanceof Error ? e.message : "Could not turn changes on."); }
          finally { setBusy(false); }
        }}>{busy ? "Turning on…" : "Turn on changes"}</button>
        : <Link className="text-button" to={`/client-settings?client_id=${clientId}`}>Open Connections</Link>}
    </div>
  );
}

/** Changes made from NOLA on this account: waiting, approved and ready, done. */
export function ChangesList({ changes, admin, clientId, onRan, busy, setBusy }: {
  changes: FbChange[]; admin: boolean; clientId: number; onRan: (message: string, ok: boolean) => void; busy: boolean; setBusy: (b: boolean) => void;
}) {
  if (!changes.length) return null;
  return (
    <section className="panel campaigns-changes">
      <div className="panel-heading"><div><h2>Changes made in NOLA</h2></div></div>
      <ul className="campaigns-change-list">
        {changes.map((c) => {
          const s = changeState(c);
          return (
            <li key={c.id}>
              <div>
                <span className="campaigns-change-head"><StatusBadge tone={s.tone}>{s.label}</StatusBadge><strong>{(c.title || c.action).replace(/^Facebook, /, "")}</strong></span>
                <p className="muted">{[c.by_name, formatDateTime(c.finished_at || c.created_at)].filter(Boolean).join(", ")}</p>
                {c.result_message && c.status !== "verified" && <p className="muted">{c.result_message}</p>}
              </div>
              {s.runnable && admin ? (
                <button type="button" className="text-button" disabled={busy} onClick={async () => {
                  if (!window.confirm("Run this approved change on Facebook now?")) return;
                  setBusy(true);
                  try { const r = await api<{ message: string }>(`/api/facebook/${clientId}/actions/${c.id}/run`, { method: "POST" }); onRan(r.message, true); }
                  catch (e) { onRan(e instanceof Error ? e.message : "The change did not run.", false); }
                  finally { setBusy(false); }
                }}>Run now</button>
              ) : c.approval_status === "pending" ? <Link className="text-button" to={`/approvals?record=${c.approval_id}`}>Open Approvals</Link> : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
